/**
 * Cliente HTTP da autenticação.
 *
 * Tudo com `credentials: 'same-origin'`: a sessão é cookie `httpOnly`, então
 * não há token para o JavaScript guardar — e não haver é a vantagem. Um XSS
 * não consegue ler o que o JavaScript não alcança, e o Safari não bloqueia o
 * cookie porque PWA e API vivem na mesma origem.
 */

const AUTH = '/api/auth';
const API = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * Corpo inteiro da resposta.
     *
     * Algumas recusas trazem o que a tela precisa mostrar para a pessoa
     * decidir — o 409 de encurtar o período devolve QUAIS dias têm conteúdo.
     * Sem isto, a tela saberia que não deu e não saberia dizer por quê.
     */
    readonly detalhe: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function pedir<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: init.body ? { 'content-type': 'application/json' } : {},
    ...init,
  });

  const texto = await res.text();
  const corpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ApiError(
      res.status,
      String(corpo['error'] ?? corpo['code'] ?? 'erro'),
      String(corpo['message'] ?? mensagemPadrao(res.status)),
      corpo,
    );
  }

  return corpo as T;
}

function mensagemPadrao(status: number): string {
  if (status === 400) return 'Não foi possível concluir com estes dados.';
  if (status === 401) return 'E-mail ou senha incorretos.';
  if (status === 403) return 'Pedido recusado.';
  if (status === 429) return 'Muitas tentativas. Espere alguns minutos e tente de novo.';
  if (status >= 500) return 'O servidor não respondeu. Tente de novo em instantes.';
  return 'Não foi possível concluir.';
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
}

export async function fetchMe(): Promise<{ user: SessionUser } | null> {
  try {
    return await pedir<{ user: SessionUser }>(`${API}/me`);
  } catch (erro) {
    if (erro instanceof ApiError && erro.status === 401) return null;
    throw erro;
  }
}

export function signIn(email: string, password: string) {
  return pedir<{ user: SessionUser }>(`${AUTH}/sign-in/email`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function signOut() {
  return pedir<unknown>(`${AUTH}/sign-out`, { method: 'POST', body: JSON.stringify({}) });
}

/**
 * Pede o link de redefinição.
 *
 * A resposta do servidor é a MESMA para e-mail cadastrado e não cadastrado —
 * é o que impede usar esta tela para descobrir quem tem conta. A tela precisa
 * respeitar isso: mostra sempre a mesma confirmação.
 */
export function requestPasswordReset(email: string, redirectTo: string) {
  return pedir<unknown>(`${AUTH}/request-password-reset`, {
    method: 'POST',
    body: JSON.stringify({ email, redirectTo }),
  });
}

/**
 * Aplica a senha nova a partir do token do e-mail.
 *
 * O token é de uso único e o servidor derruba todas as sessões ao aceitá-lo —
 * inclusive a de quem estiver logado neste aparelho.
 */
export function resetPassword(token: string, newPassword: string) {
  return pedir<{ status: boolean }>(`${AUTH}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

/**
 * Troca a senha com a senha atual — quem está logado. Diferente de
 * `resetPassword`, não derruba a sessão deste aparelho; derruba as OUTRAS,
 * porque trocar a senha costuma ser resposta a "alguém pode ter a antiga".
 */
export function changePassword(currentPassword: string, newPassword: string) {
  return pedir<{ status?: boolean }>(`${AUTH}/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions: true }),
  });
}

export interface InvitationPreview {
  invitationId: string;
  tripId: string;
  tripName: string;
  invitedByName: string;
  expiresAt: string;
  primaryDestination: string;
  startDate: string;
  endDate: string;
  /** Capa da viagem, assinada por poucos minutos; `null` sem capa. */
  coverUrl: string | null;
}

export function previewInvitation(localizador: { token?: string; code?: string }) {
  const query = new URLSearchParams(
    localizador.token ? { token: localizador.token } : { code: localizador.code ?? '' },
  );
  return pedir<InvitationPreview>(`${API}/invitations/preview?${query.toString()}`);
}

/**
 * Cadastro público — a operação A da modelagem.
 *
 * Cria só a conta. Entrar numa viagem é outra coisa, e acontece depois: ou
 * criando uma viagem, ou aceitando um convite. O convite não é, e não pode
 * virar, credencial de cadastro.
 */
export function signUp(input: { name: string; email: string; password: string }) {
  return pedir<{ user: SessionUser }>(`${AUTH}/sign-up/email`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface TripSummary {
  id: string;
  name: string;
  primaryDestination: string;
  startDate: string;
  endDate: string;
  timezone: string;
  baseCurrency: string;
  /** Já há despesas ou acertos: a moeda não muda mais. */
  currencyLocked: boolean;
  status: 'upcoming' | 'active' | 'finished' | 'archived';
  role: 'owner' | 'admin' | 'collaborator' | 'participant';
  /** Nulos até alguém preencher: viagem nova não tem hospedagem. */
  accommodationName: string | null;
  accommodationAddress: string | null;
  /** Hospedagem como lugar (Places), para o pin do mapa. */
  accommodationPlaceId: string | null;
  accommodationLatitude: number | null;
  accommodationLongitude: number | null;
  accommodationCheckIn: string | null;
  accommodationCheckOut: string | null;
  accommodationNotes: string | null;
  /** Funcionalidades desligadas por quem administra (só chaves com false). */
  features: Record<string, boolean>;
  /** Chave da imagem da viagem; a URL vem de GET /trips/:id/cover. */
  coverKey?: string | null;
}

/** Lista vazia é resposta legítima: conta nova não participa de nada. */
export function listTrips() {
  return pedir<{ trips: TripSummary[] }>(`${API}/trips`);
}

/**
 * Cria a viagem — operação B. O `id` vem do cliente para que um reenvio não
 * produza duas viagens iguais.
 */
export function createTrip(input: {
  id: string;
  name: string;
  primaryDestination: string;
  startDate: string;
  endDate: string;
  timezone: string;
  baseCurrency?: string;
}) {
  return pedir<TripSummary>(`${API}/trips`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Dia que impede o encurtamento do período, com o que há nele. */
export interface DiaComConteudo {
  date: string;
  activityCount: number;
  photoCount: number;
}

/**
 * Edita a viagem — nome, destino, período, fuso, moeda e hospedagem.
 *
 * Campo ausente não muda nada. `accommodationName: null` limpa o campo: são
 * coisas diferentes, e o corpo precisa poder dizer as duas.
 *
 * Encurtar o período sobre dias com conteúdo responde **409** com a lista dos
 * dias — a tela mostra e pede uma decisão, em vez de apagar.
 */
export function updateTrip(
  tripId: string,
  input: Partial<{
    name: string;
    primaryDestination: string;
    startDate: string;
    endDate: string;
    timezone: string;
    baseCurrency: string;
    accommodationName: string | null;
    accommodationAddress: string | null;
    accommodationPlaceId: string | null;
    accommodationLatitude: number | null;
    accommodationLongitude: number | null;
    accommodationCheckIn: string | null;
    accommodationCheckOut: string | null;
    accommodationNotes: string | null;
    summaryTime: string;
  }>,
) {
  return pedir<TripSummary>(`${API}/trips/${tripId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Liga/desliga uma funcionalidade da viagem (Owner/Admin). Avisa o grupo. */
export function setTripFeature(tripId: string, feature: string, enabled: boolean) {
  return pedir<{ features: Record<string, boolean> }>(`${API}/trips/${tripId}/features/${feature}`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export function acceptInvitationAsMe(localizador: { token?: string; code?: string }) {
  return pedir<{ tripId: string }>(`${API}/invitations/accept-as-me`, {
    method: 'POST',
    body: JSON.stringify(localizador),
  });
}
